import { StringTMap } from '@@types/common';

import { EDITOR_ID } from '~/constants';
import { Hint } from '~/hints/Hint';
import { action, comparer, computed, observable, runInAction } from 'mobx';
import { allPass, findIndex, flatten, pipe, pluck, propEq } from 'ramda';

class EditorHintStore {
  @observable
  activeIndex = -1;

  @observable
  editorId: EDITOR_ID | null = null;

  @observable.ref
  hint = null;

  @observable
  items = [];

  @observable
  commands = [];

  @observable
  isFetchingItems = false;

  @observable
  text = '';

  prevItem = null;

  backup: StringTMap<{
    activeIndex: number;
    editorId: EDITOR_ID;
    hint: null;
    items: Array<any>;
    text: string;
  }> = {};

  @computed
  get activeItem() {
    return this.items[this.activeIndex];
  }

  @computed
  get hintQuery() {
    return this.hint?.getQuery(this.text) ?? '';
  }

  @computed
  get isMainEditor() {
    return this.editorId === EDITOR_ID.MAIN;
  }

  @computed({ equals: comparer.shallow })
  get itemIds() {
    return pluck('id', this.items);
  }

  @action
  increaseActiveIndex = () => {
    if (this.items.length === this.activeIndex + 1) {
      this.setActiveIndex(0);
    } else {
      this.setActiveIndex(this.activeIndex + 1);
    }
  };

  @action
  decreaseActiveIndex = () => {
    if (this.activeIndex - 1 < 0) {
      this.setActiveIndex(this.items.length - 1);
    } else {
      this.setActiveIndex(this.activeIndex - 1);
    }
  };

  @action
  setActiveIndex = (index) => {
    this.activeIndex = index;
    this.setPrevItem(this.activeIndex);
  };

  @action
  setFetchingItems = (value: boolean) => {
    this.isFetchingItems = value;
  };

  @action
  backupStoreData = (editorId: EDITOR_ID) => {
    if (this.editorId === editorId && this.items.length > 0) {
      this.backup[editorId] = {
        activeIndex: this.activeIndex,
        editorId,
        hint: this.hint,
        items: this.items,
        text: this.text,
      };
    }
  };

  @action
  recoveryStoreData = (editorId: EDITOR_ID) => {
    if (this.backup[editorId]) {
      Object.keys(this.backup[editorId]).forEach((key) => {
        this[key] = this.backup[key];
      });
      delete this.backup[editorId];
    }
  };

  clearEditorHint = () => {
    runInAction(() => {
      this.setActiveIndex(-1);
      this.setEditorId(null);
      this.setHint(null);
      this.setText('');
      this.setItems([]);
    });
  };

  setEditorHint = ({ editorId, hint, text }: { editorId: EDITOR_ID; hint: Hint; text: string }) => {
    runInAction(() => {
      this.setEditorId(editorId);
      this.setHint(hint);
      this.setText(text);
    });
  };

  @action
  setCommands = (commands) => {
    this.commands = commands;
    pipe(pluck('command'), flatten, this.setCommandItems)(commands);
  };

  @action
  setCommandItems = (items) => {
    this.items = items;
    this.setPrevActiveIndex(items);
  };

  @action
  setItems = (items) => {
    this.items = items;
    this.setActiveIndex(0);
  };

  @action
  setEditorId = (editorId: EDITOR_ID | null) => {
    this.editorId = editorId;
  };

  @action
  setHint = (hint: any) => {
    this.hint = hint;
  };

  @action
  setText = (text: string) => {
    this.text = text;
  };

  setPrevItem = (index) => {
    if (index === -1 || !this.items.length) return;
    this.prevItem = this.items[index];
  };

  setPrevActiveIndex = (items: { id: string; name: string }[]) => {
    let index = 0;
    if (this.prevItem && items.length && this.text !== '/') {
      index = findIndex<{ id: string; name: string }>(
        allPass([propEq('id', this.prevItem.id), propEq('name', this.prevItem.name)])
      )(items);
      index = index === -1 ? 0 : index;
    }
    this.setActiveIndex(index);
  };

  clearPrevItem = () => {
    this.prevItem = null;
  };
}

export default EditorHintStore;

export const editorHintStore = new EditorHintStore();
